import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import type {SignupSectionSliceV2Primary} from 'prismicio-types'
import type {FormEventHandler} from 'react'
import type {UseFormRegister, UseFormStateReturn} from 'react-hook-form'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {StarRatings} from 'slices/HeroV2/RatingsSection/StarRatings'
import {ButtonV2} from 'src/common/ButtonV2'
import {Container} from 'src/common/Container'
import {Icon} from 'src/common/Icon'

type FormData = {
  firstName: string
  lastName: string
  email: string
  checkbox: boolean
}

const darkBackgroundColors = ['Moss Black', 'Green (legacy)', 'Green/500', 'Dark Green']

/**
 * Props for `SignupSection`.
 */
export type SignupSectionV2Props = {
  slice: {
    primary: SignupSectionSliceV2Primary
    variation: string
    slice_type: string
  }
  formHandler: FormEventHandler<HTMLFormElement>
  register: UseFormRegister<FormData>
  formState: UseFormStateReturn<FormData>
  pageUid: string
}

/**
 * Component for "SignupSection" Slices.
 */
export const SignupSectionV2 = ({
  slice: {primary, slice_type, variation},
  formHandler,
  register,
  formState: {isSubmitSuccessful, isSubmitting, isValid},
  pageUid,
}: SignupSectionV2Props): JSX.Element => {
  const {linkResolver} = useLinkResolver()
  const hasDarkBackground = darkBackgroundColors.includes(primary.background_color || '')

  return (
    <Container
      className="scroll-mt-16 px-5 md:px-10 lg:px-5"
      data-slice-type={slice_type}
      data-slice-variation={variation}
      id="sign-up-section"
      backgroundColor={primary.section_background_color}
    >
      <div
        className={cn(
          'flex w-full min-w-0 max-w-[1200px] flex-col items-center gap-12 rounded-lg p-6 md:gap-10 md:px-8 lg:flex-row lg:justify-between lg:p-16',
          {
            'bg-beige-100': primary.background_color === 'Beige/100',
            'bg-beige-200': primary.background_color === 'Beige/200',
            'bg-beige-300': primary.background_color === 'Beige/300',
            'bg-moss-black': primary.background_color === 'Moss Black',
            'bg-moss-green-500': primary.background_color === 'Green/500',
            'bg-5a7269': primary.background_color === 'Green (legacy)',
            'bg-green-dark': primary.background_color === 'Dark Green',
          },
        )}
      >
        <div className="flex h-full flex-col justify-between max-lg:gap-6">
          <div className="flex flex-col gap-3 lg:w-[480px] lg:max-w-[544px]">
            <PrismicRichText
              components={{
                heading2: ({children}) => (
                  <h2
                    className={cn('text-h3 lg:text-start', {
                      'text-moss-black': primary.background_color !== 'Moss Black',
                      'text-moss-white': hasDarkBackground,
                    })}
                  >
                    {children}
                  </h2>
                ),
              }}
              field={primary.title}
            />

            <PrismicRichText
              components={{
                paragraph: ({children}) => (
                  <p
                    className={cn('text-tg-body-l', {
                      'text-moss-black': !hasDarkBackground,
                      'text-moss-white':
                        primary.background_color === 'Moss Black' || primary.background_color === 'Green (legacy)',
                      'text-moss-white/[.6]': primary.background_color === 'Green/500',
                      'text-moss-white/[70%]': primary.background_color === 'Dark Green',
                    })}
                  >
                    {children}
                  </p>
                ),
              }}
              field={primary.description}
            />
          </div>
          {isFilled.group(primary.rating) && (
            <div className="self-start max-lg:-order-1">
              <StarRatings review={primary.rating[0]} darkBackground={hasDarkBackground} />
            </div>
          )}
        </div>

        <div className="flex w-full flex-col gap-6 md:size-full lg:max-w-[320px]">
          <form
            className={cn(
              'grid w-full grid-cols-1 place-content-center gap-3 text-moss-black transition-all duration-300 md:w-[320px] md:grid-cols-2 md:self-center lg:max-w-[320px]',
              {
                hidden: isSubmitSuccessful,
              },
            )}
            onSubmit={formHandler}
          >
            <label className="max-md:col-span-2">
              <div className="hidden">{primary.first_name_label || 'First Name'}</div>

              <input
                className="h-10 w-full rounded-[4px] border border-dcdbda bg-moss-white px-4 text-14 leading-5 outline-none transition-shadow placeholder:text-8f8d8b focus:shadow-card"
                placeholder={primary.first_name_placeholder || 'Andrea'}
                type="text"
                {...register('firstName', {required: 'Please enter your first name.'})}
              />
            </label>

            <label className="max-md:col-span-2">
              <div className="hidden">{primary.last_name_label || 'Last Name'}</div>

              <input
                className="h-10 w-full rounded-[4px] border border-dcdbda bg-moss-white px-4 text-14 leading-5 outline-none transition-shadow placeholder:text-8f8d8b focus:shadow-card"
                placeholder={primary.last_name_placeholder || 'Smith'}
                type="text"
                {...register('lastName', {required: 'Please enter your last name.'})}
              />
            </label>

            <label className="col-span-2">
              <div className="hidden">{primary.email_label || 'Your work email'}</div>

              <input
                className="h-10 w-full rounded-[4px] border border-dcdbda bg-moss-white px-4 text-14 leading-5 outline-none transition-shadow placeholder:text-8f8d8b focus:shadow-card"
                placeholder={primary.email_placeholder || 'email@company.com'}
                type="text"
                {...register('email', {required: 'Please enter your email.'})}
              />
            </label>

            <label className="col-span-2 grid grid-flow-col justify-start gap-x-3 text-14 leading-5">
              <div className="grid">
                <input
                  className="size-3 appearance-none rounded-[4px] border border-dcdbda bg-ffffff outline-none transition-shadow span-full placeholder:text-8f8d8b checked:border-4e7e6c checked:bg-4e7e6c focus:shadow-card"
                  type="checkbox"
                  {...register('checkbox', {required: 'Please confirm this.'})}
                />
                <Icon className="size-3 text-moss-white span-full" name="tick" />
              </div>

              <PrismicRichText
                components={{
                  paragraph: ({children}) => (
                    <p className="text-tg-body-s font-medium leading-[normal] text-moss-white">{children}</p>
                  ),
                  hyperlink: ({children, node}) => (
                    <Link
                      className="text-4e7e6c"
                      href={linkResolver(node.data)}
                      {...(node.data.link_type === 'Web' && node.data.target && {target: node.data.target})}
                    >
                      {children}
                    </Link>
                  ),
                }}
                field={primary.checkbox_label}
              />
            </label>

            <div className="col-span-full pt-3">
              <ButtonV2
                className="justify-self-center max-md:!h-12 max-md:w-full lg:justify-self-start"
                variant="primary"
                trackingData={{
                  mktgId: 'form-submit-button',
                  location: `component-signup-section`,
                  dataType: 'signup-button',
                  pageName: pageUid,
                  label: primary.button_label!,
                }}
                type="submit"
                disabled={!isValid}
              >
                {isSubmitting ? primary.button_submitting_label : primary.button_label}
              </ButtonV2>
            </div>
          </form>
          <div
            className={cn(
              'flex h-[196px] w-full flex-col items-center justify-center gap-1 rounded-lg bg-moss-white/[7%] max-md:px-5 max-md:py-6 max-md:text-center md:max-lg:min-h-[174px] md:max-lg:max-w-[384px] md:max-lg:self-center',
              {
                hidden: !isSubmitSuccessful,
              },
            )}
          >
            <Icon className="size-12 text-green-neon" name="filled-check-v2" />
            <div className="flex flex-col text-center">
              <h3 className="text-tg-body-l text-green-neon">{primary.success_title || 'Super!'}</h3>

              <PrismicRichText
                components={{
                  strong: ({children}) => <span className="text-tg-body-l text-green-neon">{children}</span>,
                  paragraph: ({children}) => <p className="text-tg-body-l text-moss-white">{children}</p>,
                }}
                field={primary.success_description}
              />
            </div>
          </div>
        </div>
      </div>
    </Container>
  )
}
